// ============================================
// Image Upload Hook
// ============================================

import { useState } from 'react';
import { tokenStorage } from '../config/api';
import { env } from '../config/env';

export type EntityType = 'product' | 'category' | 'supplier' | 'customer' | 'store';

interface UploadImageResult {
  file_url: string;
  object_name: string;
  entity_type: string;
  content_type: string;
  size: number;
}

interface UploadImageResponse {
  data: UploadImageResult;
  code: number;
  message: string;
}

export function useUploadImage() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadImage = async (file: File, entityType: EntityType): Promise<string | null> => {
    setIsUploading(true);
    setUploadError(null);

    try {
      const token = await tokenStorage.getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);

      const response = await fetch(`${env.apiBaseUrl}/uploads/images/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const msg = (errorData as { message?: string }).message ?? `Upload failed (${response.status})`;
        throw new Error(msg);
      }

      const result: UploadImageResponse = await response.json();
      console.log('[useUploadImage] upload success:', result);

      // Rewrite file_url to use the same host as the API base URL
      // Backend may return localhost but we need the actual server host
      let fileUrl = result.data.file_url;
      try {
        const apiOrigin = new URL(env.apiBaseUrl).origin;
        const parsedFileUrl = new URL(fileUrl);
        if (parsedFileUrl.origin !== apiOrigin) {
          fileUrl = apiOrigin + parsedFileUrl.pathname + parsedFileUrl.search;
        }
      } catch {
        // If URL parsing fails, use as-is
      }

      return fileUrl;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(msg);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadImage, isUploading, uploadError, setUploadError };
}
